import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useDashboardPermissions } from '@/hooks/useDashboardPermissions';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { StatCard } from './StatCard';
import { FolderKanban, Building2, MessageSquare, Home, HardHat, CheckCircle, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export function DeveloperDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { permissions: perms } = useDashboardPermissions();

  const { data: stats, isLoading } = useQuery({
    queryKey: ['developer-dashboard-stats', user?.id],
    queryFn: async () => {
      const { data: devProfile } = await supabase
        .from('developer_profiles')
        .select('id')
        .eq('user_id', user!.id)
        .single();

      if (!devProfile) return { totalProjects: 0, approvedProjects: 0, pendingProjects: 0, totalUnits: 0, availableUnits: 0, totalLeads: 0 };

      const { data: projects } = await supabase
        .from('projects')
        .select('id, total_units, available_units, approval_status, status')
        .eq('developer_id', devProfile.id);

      const totalProjects = projects?.length || 0;
      const approvedProjects = projects?.filter(p => p.approval_status === 'approved').length || 0;
      const pendingProjects = projects?.filter(p => p.approval_status === 'pending').length || 0;
      const totalUnits = projects?.reduce((sum, p) => sum + (p.total_units || 0), 0) || 0;
      const availableUnits = projects?.reduce((sum, p) => sum + (p.available_units || 0), 0) || 0;

      const projectIds = projects?.map(p => p.id) || [];
      let totalLeads = 0;
      if (projectIds.length > 0) {
        const { count } = await supabase
          .from('leads')
          .select('*', { count: 'exact', head: true })
          .in('project_id', projectIds);
        totalLeads = count || 0;
      }

      return { totalProjects, approvedProjects, pendingProjects, totalUnits, availableUnits, totalLeads };
    },
    enabled: !!user,
  });

  const { data: recentProjects } = useQuery({
    queryKey: ['developer-recent-projects', user?.id],
    queryFn: async () => {
      const { data: devProfile } = await supabase
        .from('developer_profiles')
        .select('id')
        .eq('user_id', user!.id)
        .single();
      if (!devProfile) return [];
      const { data } = await supabase
        .from('projects')
        .select('id, name, city, status, approval_status, total_units, available_units, price_min, price_max, currency')
        .eq('developer_id', devProfile.id)
        .order('created_at', { ascending: false })
        .limit(5);
      return data || [];
    },
    enabled: !!user,
  });

  const statusIcons: Record<string, any> = {
    upcoming: { icon: Clock, color: 'text-blue-500', bg: 'bg-blue-100' },
    under_construction: { icon: HardHat, color: 'text-orange-500', bg: 'bg-orange-100' },
    ready: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-100' },
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-xl font-bold">Developer Dashboard</h2>
        <p className="text-sm text-muted-foreground mt-1">Manage your projects, track units, and monitor inquiries.</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard value={stats?.totalProjects || 0} label="Total Projects" variant="sale" progress={Math.min((stats?.totalProjects || 0) * 20, 100)} />
        <StatCard value={stats?.totalUnits || 0} label="Total Units" variant="rent" progress={stats?.totalUnits ? Math.round(((stats?.availableUnits || 0) / stats.totalUnits) * 100) : 0} />
        <StatCard value={stats?.availableUnits || 0} label="Available Units" variant="customer" progress={stats?.totalUnits ? Math.round(((stats?.availableUnits || 0) / stats.totalUnits) * 100) : 0} />
        <StatCard value={stats?.totalLeads || 0} label="Project Inquiries" variant="city" progress={Math.min((stats?.totalLeads || 0) * 5, 100)} />
      </div>

      {/* Project Status Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="rounded-2xl border-0 shadow-sm">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-green-100 flex items-center justify-center">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <div className="text-2xl font-bold">{stats?.approvedProjects || 0}</div>
              <div className="text-xs text-muted-foreground">Approved Projects</div>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-0 shadow-sm">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-yellow-100 flex items-center justify-center">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <div className="text-2xl font-bold">{stats?.pendingProjects || 0}</div>
              <div className="text-xs text-muted-foreground">Pending Approval</div>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-0 shadow-sm">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Building2 className="h-6 w-6 text-primary" />
            </div>
            <div>
              <div className="text-2xl font-bold">{(stats?.totalUnits || 0) - (stats?.availableUnits || 0)}</div>
              <div className="text-xs text-muted-foreground">Units Sold/Reserved</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Projects */}
      <Card className="rounded-2xl border-0 shadow-sm">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-sm flex items-center gap-2">
              <FolderKanban className="h-4 w-4 text-primary" /> My Projects
            </h3>
            {perms.canAddProject && (
              <button onClick={() => navigate('/dashboard/projects/new')} className="text-xs text-primary font-semibold hover:underline">Add New Project</button>
            )}
          </div>
          {recentProjects && recentProjects.length > 0 ? (
            <div className="space-y-3">
              {recentProjects.map((proj: any) => {
                const si = statusIcons[proj.status] || statusIcons.upcoming;
                const StatusIcon = si.icon;
                return (
                  <div key={proj.id} className="flex items-center gap-3 p-3 rounded-xl bg-muted/50">
                    <div className={`h-10 w-10 rounded-lg ${si.bg} flex items-center justify-center shrink-0`}>
                      <StatusIcon className={`h-5 w-5 ${si.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">
                        {typeof proj.name === 'object' ? (proj.name as any)?.en || 'Project' : 'Project'}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {proj.city} · {proj.total_units || 0} units · {proj.available_units || 0} available
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${proj.approval_status === 'approved' ? 'bg-green-100 text-green-700' : proj.approval_status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                        {proj.approval_status}
                      </span>
                      <div className="text-[10px] text-muted-foreground mt-1 capitalize">{proj.status?.replace('_', ' ')}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">No projects yet. Create your first project to get started.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
